<template>
  <div class="cx-date-picker-activator">
    <cx-label v-if="label" class="cx-date-picker-activator__label" :label="label"/>
    <div class="cx-date-picker-activator__input-container" :disabled="disabled">
      <div class="cx-date-picker-activator__input-wrapper" :style="hideOnLoading">
        <span class="cx-date-picker-activator__input-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true" class="cx-date-picker-activator__input-icon">
            <path d="M29.333 8c0-2.208-1.792-4-4-4h-18.667c-2.208 0-4 1.792-4 4v18.667c0 2.208 1.792 4 4 4h18.667c2.208 0 4-1.792 4-4v-18.667zM26.667 8v18.667c0 0.736-0.597 1.333-1.333 1.333 0 0-18.667 0-18.667 0-0.736 0-1.333-0.597-1.333-1.333 0 0 0-18.667 0-18.667 0-0.736 0.597-1.333 1.333-1.333 0 0 18.667 0 18.667 0 0.736 0 1.333 0.597 1.333 1.333z"/>
            <path d="M20 2.667v5.333c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-5.333c0-0.736-0.597-1.333-1.333-1.333s-1.333 0.597-1.333 1.333z"/>
            <path d="M9.333 2.667v5.333c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-5.333c0-0.736-0.597-1.333-1.333-1.333s-1.333 0.597-1.333 1.333z"/>
            <path d="M4 14.667h24c0.736 0 1.333-0.597 1.333-1.333s-0.597-1.333-1.333-1.333h-24c-0.736 0-1.333 0.597-1.333 1.333s0.597 1.333 1.333 1.333z"/>
          </svg>
        </span>
        <input
          class="cx-date-picker-activator__activator-input"
          type="text"
          :value="value"
          readonly
          :disabled="disabled"
          :placeholder="placeholder"/>
      </div>
      <cx-spinner :size="15" :width="1" v-if="loading"></cx-spinner>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import CxSpinner from '../CxSpinner.vue';
import CxLabel from '../CxLabel.vue';

interface Props {
  value?: string,
  disabled?: boolean,
  loading?: boolean
  placeholder?: string,
  label?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  loading: false,
});

const hideOnLoading = computed(() => {
  return props.loading ? 'visibility: hidden;' : 'visibility: visible;';
});
</script>

<style lang="less">
@import '../../common.less';

.cx-date-picker-activator {
  @root: cx-date-picker-activator;
  font-size: @appFontSize;
  font-family: @appFontFamily;
  color: @textColor;

  .@{root}__label {
    font-size: @appFontSize;
    font-weight: @appFontWeight;
    padding: 0 0 3px 12px;
    color: @textColorLowlight;
    text-align: start;
  }

  .@{root}__input-container {
    background-color: @inputBackground;
    display: flex;
    border-radius: @inputBorderRadius;
    caret-color: @textColor;
    color: @textColor;
    width: 100%;
    position: relative;

    &[disabled="true"] {
      background-color: @inputBackgroundDisabled;
    }

    &:hover:not([disabled="true"]) {
      background-color: @inputBackgroundHover;
    }

    .@{root}__input-wrapper {
      display: flex;
      flex: 1 1 auto;
      padding: 8px 8px 7px 12px;
    }
  }

  .@{root}__activator-input {
    padding-top: 1px;
    font-size: @appFontSize;
    font-weight: @appFontWeight;
    border-radius: @inputBorderRadius;
    background-color: inherit;
    color: inherit;
    outline: none;
    border: none;
    padding-left: 8px;
    width: 100%;

    &:disabled {
      caret-color: @textColorDisabled;
      color: @textColorDisabled;
    }

    &:hover {
      &:not([disabled="true"]) {
        caret-color: @textColorHighlight;
        color: @textColorHighlight;
      }
    }

    &:focus {
      caret-color: @textColorHighlight;
      color: @textColorHighlight;
      outline: none;
      border: none;
    }
  }

  .@{root}__input-icon-wrapper {
    display: flex;
    align-items: center;
  }

  .@{root}__input-icon {
    width: var(--dp-font-size);
    stroke-width: 0;
  }

  .cx-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

</style>